/**
 * Encodes/decodes the "FDA Sources view, unlinked-only" deep-link state
 * (source + unlinked flag) to/from flat URL query params
 * (?fdaSource=<source>&unlinked=true), separate from searchFilterCodec.ts's
 * base64-encoded AdvancedSearchQuery `?filter=` param. See
 * docs/features/fda-source-record-browser.md Architecture Decision 4 --
 * "source + unlinked" is two flat enum-ish values, not a SearchRule, so a
 * base64 blob adds nothing here.
 */

import type { FdaSourceKey } from '../services/api/domains/products/types';

const FDA_SOURCE_KEYS: FdaSourceKey[] = ['ob', 'ndc', 'dmf'];

function isFdaSourceKey(value: string | null): value is FdaSourceKey {
  return value !== null && (FDA_SOURCE_KEYS as string[]).includes(value);
}

/** Encodes a source into the `fdaSource=<source>&unlinked=true` query string. */
export function encodeFdaSourceUnlinkedParams(source: FdaSourceKey): string {
  return `fdaSource=${source}&unlinked=true`;
}

/**
 * Decodes `fdaSource`/`unlinked` from a URLSearchParams instance. Returns
 * null when `fdaSource` is missing/unrecognized or `unlinked` isn't
 * literally `'true'` -- fails closed, never throws, mirroring
 * searchFilterCodec.ts's decodeFilter contract.
 */
export function decodeFdaSourceUnlinkedParams(
  searchParams: URLSearchParams
): { source: FdaSourceKey } | null {
  const source = searchParams.get('fdaSource');
  const unlinked = searchParams.get('unlinked');
  if (unlinked !== 'true' || !isFdaSourceKey(source)) {
    return null;
  }
  return { source };
}
